import React, { useState } from "react";
import { Box } from "@mui/material";
import { DotLegalDialog, DotLegalHeader, DotLegalMultiSelect, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import RecurrenceSection from "../../annualWheel/components/RecurrenceSection.tsx";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useAnnualWheelDialogStyles } from "../../annualWheel/annualWheelDialog/AnnualWheelDialog.styles.ts";
import { useAnnualWheelActivityStandardTemplateDialog } from "./AnnualWheelActivityStandardTemplateDialog.hooks.ts";
import { AnnualWheelActivityStandardTemplateModel } from "./AnnualWheelActivityStandardTemplate.types.ts";
import AnnualWheelActivityStandardTemplateLanguageData from "./AnnualWheelActivityStandardTemplateLanguageData.tsx";
import { useComplianceAreaQuery } from "../../common/hooks/useSelectableItemQueries.ts";
import DotLegalTabs from "../../common/components/dotLegalTabs/DotLegalTabs.tsx";
import { TabContent } from "../../common/components/dotLegalTabs/DotLegalTabs.types.ts";
import ImageSelector from "./ImageSelector.tsx";
import { useRecurringIntervalTypeSelectOptions } from "../../annualWheel/AnnualWheel.hooks.ts";
import { useFeatureFlags } from "../../auth/planProvider/PlanProvider.tsx";

export interface IAnnualWheelActivityStandardTemplateDialogProps {
    model?: AnnualWheelActivityStandardTemplateModel;
    onCancel: () => void;
    onSaved: () => void;
}

function AnnualWheelActivityStandardTemplateDialog(props: IAnnualWheelActivityStandardTemplateDialogProps) {
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const featureFlag = useFeatureFlags();

    const styles = useAnnualWheelDialogStyles();
    const complianceAreaQuery = useComplianceAreaQuery();
    const recurringIntervalTypeOptions = useRecurringIntervalTypeSelectOptions();

    const { translateString } = useTranslation();
    const { onSave, isEdit, templateModel, modelUpdater, annualWheelActivityGeneralTypes, isSaving, showError, isIntervalInvalid } =
        useAnnualWheelActivityStandardTemplateDialog(props);

    let tabContents: TabContent[] = ["en", "da"].map((lang, i) => {
        let langModel = templateModel.languages.find((l) => l.twoLetterIsoLanguage === lang)!;
        return {
            label: lang.toUpperCase(),
            index: i,
            content: (
                <Box>
                    <AnnualWheelActivityStandardTemplateLanguageData
                        key={lang}
                        name={langModel.name}
                        description={langModel.description}
                        subTasks={langModel.subTasks}
                        onNameChange={(name) => modelUpdater.onNameChange(lang, name)}
                        onDescriptionChange={(description) => modelUpdater.onDescriptionChange(lang, description)}
                        onSubTaskChange={(tasks) => modelUpdater.onSubTaskChange(lang, tasks)}
                        showError={showError}
                    />
                </Box>
            ),
        };
    });
    return (
        <DotLegalDialog
            header={isEdit ? translateString("editTemplate") : translateString("createTemplate")}
            buttonOkText={isEdit ? translateString("save") : translateString("create")}
            open
            size={"xl"}
            onDialogClose={props.onCancel}
            onOkClick={onSave}
            secondaryButtonText={translateString("cancel")}
            onSecondaryButtonClick={props.onCancel}
            buttonRight
            inProgress={isSaving}
        >
            <Box sx={styles.contentWrapper}>
                <Box sx={styles.formSpacing}>
                    <DotLegalTabs
                        tabStyle={"secondary"}
                        selectedTab={selectedTabIndex}
                        setSelectedTab={setSelectedTabIndex}
                        content={tabContents}
                        fullWidth
                        border
                    />

                    <RecurrenceSection
                        interval={templateModel.recurringInterval}
                        intervalType={templateModel.recurringIntervalType}
                        intervalTypeOptions={recurringIntervalTypeOptions}
                        onIntervalChange={modelUpdater.onIntervalChange}
                        onIntervalTypeChange={(t) => modelUpdater.onRecurrenceTypeChange(t)}
                        onSingularChange={modelUpdater.onSingularActivityChange}
                        showError={showError}
                        isIntervalInvalid={isIntervalInvalid}
                    />
                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5}>
                        {translateString("annualWheelNotifications")}
                    </DotLegalHeader>
                    <Box sx={styles.content}>
                        <Box sx={styles.notificationPeriod}>
                            <Box sx={styles.numberField}>
                                <DotLegalTextField
                                    onlyNumber={"integer"}
                                    label={""}
                                    value={templateModel.notificationPeriodInDays?.toString() ?? null}
                                    debounce={false}
                                    onChange={modelUpdater.onNotificationPeriodInDaysChange}
                                    noMargin
                                />
                            </Box>
                            <span>{translateString("days").toLowerCase()}</span>
                        </Box>
                    </Box>

                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5} toolTip={translateString("customIdToolTip")}>
                        {translateString("annualWheelCustomIdHeader")}
                    </DotLegalHeader>
                    <DotLegalTextField label={""} value={templateModel.customId ?? null} debounce={false} noMargin onChange={modelUpdater.onCustomIdChange} />

                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5}>
                        {translateString("addAnnualWheelActivityGeneralType")}
                    </DotLegalHeader>
                    <DotLegalSelect
                        selectedItem={templateModel.generalType?.toString() ?? ""}
                        options={annualWheelActivityGeneralTypes()}
                        onChange={(generalType) => modelUpdater.onGeneralTypeChange(generalType)}
                        placeholder={""}
                        label={""}
                        noMargin
                        noOptionsLabel={translateString("noOptions")}
                    />

                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5} toolTip={translateString("complianceAreaToolTip")}>
                        {translateString("areasForAnnualWheelTask")}
                    </DotLegalHeader>
                    <DotLegalMultiSelect
                        label={""}
                        options={complianceAreaQuery.data}
                        isLoading={complianceAreaQuery.isLoading}
                        chosenOptions={templateModel.complianceAreaIds}
                        placeholder={""}
                        onChange={modelUpdater.onComplianceAreasChange}
                        noMargin
                        noOptionsLabel={translateString("noOptions")}
                    />

                    <ImageSelector
                        containerUrl={"https://stdotlegalpublic.blob.core.windows.net/annual-wheel-activity-template-images"}
                        onImageSelect={modelUpdater.onImageChange}
                        selectedImageUrl={templateModel.imageUrl}
                    />
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default AnnualWheelActivityStandardTemplateDialog;
